import { FormControlLabel, Typography } from "@mui/material";
import { CoreModal, CoreRadioGroup, CoreTextBox, RadioOption } from "../../core";
import i18nPackage from "../../i18n/i18n";
import styles from "./SaveProfileModal.module.css";
import { useId, useState } from "react";
import { useMobileLayout } from "../../hooks";
import { ProctoringOptionProfileType } from "@verity/database/prisma";
import WarningIcon from "../images/warning.svg?react";
import { Trans } from "react-i18next";
import { useEditQuizStyles } from "../components";
import { ResponsiveModalBody } from "../components/EditQuizModalComponents";
import { PROFILE_CONFIG } from "../edit-quiz-steps/StepIconConfig";

export type PublishLocation = Exclude<ProctoringOptionProfileType, "assessment" | "preset">;

function ProfileRadioRow({ type }: { type: PublishLocation }) {
	const labelKey =
		type === "institute" ? "instituteDescription" : type === "course" ? "courseDescription" : "userDescription";
	const ProfileIcon = PROFILE_CONFIG[type].Icon;
	return (
		<div className={styles.profileRadioRow}>
			<div
				className={`${styles.iconWrapper} ${styles[type]}`}
				style={{ background: PROFILE_CONFIG[type].bgColor, color: PROFILE_CONFIG[type].svgColor }}
			>
				{<ProfileIcon />}
			</div>

			<Typography component={"span"} className={styles.description}>
				<Trans
					i18n={i18nPackage}
					i18nKey={`editQuizPage.saveProfileModalTSX.${labelKey}`}
					components={{ b: <strong /> }}
				/>
			</Typography>
		</div>
	);
}

export type SaveProfileModalProps = {
	onClose: () => void;
	onSave: (profileName: string, publishLocation: PublishLocation | undefined) => void;
	hasInstituteProfilePermissions: boolean;
	canPublishToCourse: boolean;
	isThirdPartyEnabled: boolean;
};

export default function SaveProfileModal({
	onClose,
	onSave,
	hasInstituteProfilePermissions,
	isThirdPartyEnabled,
	canPublishToCourse,
}: SaveProfileModalProps) {
	const { getModalInModalProps } = useEditQuizStyles();
	const [profileName, setProfileName] = useState<string>("");
	const [publishLocation, setPublishLocation] = useState<PublishLocation>("user");
	const isMobileLayout = useMobileLayout();
	const radioGroupAriaId = useId();

	const radioOptions: RadioOption[] = [
		...(hasInstituteProfilePermissions
			? [
					{
						value: "institute",
						label: <ProfileRadioRow type="institute" />,
					},
				]
			: []),
		...(canPublishToCourse
			? [
					{
						value: "course",
						label: <ProfileRadioRow type="course" />,
					},
				]
			: []),

		{
			value: "user",
			label: <ProfileRadioRow type="user" />,
		},
	];

	const title = i18nPackage.t("editQuizPage.saveProfileModalTSX.title");

	return (
		<CoreModal
			{...getModalInModalProps(isMobileLayout, title)}
			open
			onClose={onClose}
			minWidth={isMobileLayout ? undefined : "min(80%, 760px)"}
			widthFixed
			padding={isMobileLayout ? undefined : "1rem 1.5rem 0 1.5rem"}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.saveProfileModalTSX.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.saveProfileModalTSX.save"),
					color: "primary",
					disabled: !profileName,
					onClick: () => onSave(profileName, publishLocation),
				},
			]}
		>
			<ResponsiveModalBody title={title} description={i18nPackage.t("editQuizPage.saveProfileModalTSX.description")}>
				<div className={styles.saveProfileContinaer}>
					<div className={styles.warningWrapper}>
						<div className={styles.warningIconContainer}>
							<WarningIcon />
						</div>
						<Typography>
							<Trans
								i18n={i18nPackage}
								i18nKey={`editQuizPage.saveProfileModalTSX.${isThirdPartyEnabled ? "warning" : "warningNoThirdParty"}`}
								components={{ b: <strong /> }}
							/>
						</Typography>
					</div>
					<div className={styles.section}>
						<Typography className={styles.title}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.details")}
						</Typography>
						<Typography className={styles.description}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.detailsDescription")}
						</Typography>
						<FormControlLabel
							label={i18nPackage.t("editQuizPage.saveProfileModalTSX.profileName")}
							control={
								<CoreTextBox
									value={profileName}
									autoComplete="off"
									inputProps={{ maxLength: 30 }}
									placeholder={i18nPackage.t("editQuizPage.saveProfileModalTSX.enterProfileName")}
									onChange={(event) => setProfileName(event.target.value)}
									className={styles.profileNameTextfield}
								/>
							}
							labelPlacement="start"
							className={styles.profileNameLabel}
						/>
					</div>
					<div className={styles.section}>
						<Typography className={styles.title}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.saveLocation")}
						</Typography>
						<Typography className={styles.description} id={radioGroupAriaId}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.saveLocationDescription")}
						</Typography>

						<CoreRadioGroup
							onChange={(_e, value) => setPublishLocation(value as typeof publishLocation)}
							radioGroupAriaLabelledby={radioGroupAriaId}
							radioOptions={radioOptions}
							selectedValue={publishLocation}
							className={styles.profileRadioGroup}
						/>
					</div>
				</div>
			</ResponsiveModalBody>
		</CoreModal>
	);
}
